// One AI content check — what the employee submitted, what the AI decided about it
// against the Connect content policy, and the moderator decision its state still allows.
//
// ── WHY THIS IS NOT ReviewModal ─────────────────────────────────────────────
//
// ReviewModal (SocialModerationPage) opens a post or comment that EXISTS in the feed
// tables and reads it through getItem. A blocked attempt was never inserted — blocked
// content must not touch social_posts / social_comments, or it would feed red-flag
// metrics, daily caps and history — so there is no post id to hand getItem, and its
// photos are orphan uploads /social-moderation/media/:id cannot serve. This modal reads
// the REVIEW ROW instead: its content and media are a snapshot of what was submitted,
// served by the review's own media endpoint. A held post opened from All posts still
// goes through ReviewModal; that modal links here for the decision.
//
// ── WHICH BUTTONS, AND WHY ONLY THOSE ───────────────────────────────────────
//
// The offered actions follow the row's state exactly as the decide endpoint guards it:
//   • held + pending            → Approve and publish / Reject
//   • live + pending            → Looks fine / Take down   (monitor flags, "Not checked")
//   • blocked + no feedback yet → AI was right / AI was wrong   (feedback only)
// Anything else has already been decided (or overtaken by the author editing or
// deleting it) and renders the decision record instead. The server re-checks every
// transition against the stored status, so a stale modal can only ever get a 409, which
// is shown inline and followed by a reload — never a silent success.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X, Loader2, AlertCircle, CheckCircle2, ShieldOff, ThumbsUp, ThumbsDown, Sparkles, Image as ImageIcon,
} from 'lucide-react';
import AuthedImage, { useAuthedImageUrl } from '../AuthedImage';
import ImageLightbox from '../ImageLightbox';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { BlockLoader } from '../loading';
import {
  connectPolicyAPI,
  connectPolicyErrorMessage,
  connectPolicyErrorStatus,
  parseServerTimestamp,
  type AiReviewAction,
  type AiReviewDetail,
  type AiReviewRow,
  type Violation,
} from '../../services/connectPolicyApi';

// ─────────────────────────────────────────────────────────────────────────────
// Shared presentation — also used by AiReviewTab and ReviewModal's AI section, so a
// verdict reads the same wherever it appears.

export const AI_TARGET_LABEL: Record<string, string> = {
  post: 'Post',
  post_edit: 'Edit',
  reshare: 'Repost',
  comment: 'Comment',
};

export const AI_TARGET_CLS: Record<string, string> = {
  post: 'bg-indigo-50 text-indigo-700',
  post_edit: 'bg-violet-50 text-violet-700',
  reshare: 'bg-teal-50 text-teal-700',
  comment: 'bg-sky-50 text-sky-700',
};

const DECISION_META: Record<string, { label: string; cls: string }> = {
  allow: { label: 'Allowed', cls: 'bg-green-100 text-green-700' },
  hold: { label: 'Held', cls: 'bg-amber-100 text-amber-700' },
  block: { label: 'Blocked', cls: 'bg-red-100 text-red-700' },
  // An AI outage, timeout or unreadable photo. Grey, not red: nothing is wrong with the
  // content as far as anyone knows — it simply still needs a human look.
  error: { label: 'Not checked', cls: 'bg-gray-100 text-gray-600' },
};

/** The AI's verdict, plus a "Monitor" tag when that verdict was only logged. In monitor
 *  mode a "Blocked" chip describes what enforce mode WOULD have done — the post itself
 *  went out — and without the tag a moderator would read it as a blocked post. */
export function AiDecisionChip({ decision, mode }: { decision: string; mode?: string | null }) {
  const meta = DECISION_META[decision] ?? { label: decision, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
        {meta.label}
      </span>
      {mode === 'monitor' && (
        <span
          className="inline-flex whitespace-nowrap rounded-full border border-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500"
          title="Monitor mode — this was published anyway. The chip shows what enforce mode would have done."
        >
          Monitor
        </span>
      )}
    </span>
  );
}

const MOD_STATUS_META: Record<string, { label: string; cls: string; title?: string }> = {
  pending: { label: 'Needs review', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
  dismissed: { label: 'Looks fine', cls: 'bg-green-100 text-green-700' },
  confirmed: { label: 'AI was right', cls: 'bg-blue-50 text-blue-700' },
  overturned: { label: 'AI was wrong', cls: 'bg-purple-50 text-purple-700' },
  superseded: {
    label: 'Superseded', cls: 'bg-gray-100 text-gray-600',
    title: 'The author edited or deleted it before anyone decided',
  },
};

/** 'none' means different things per outcome: an allowed post needs nothing, while a
 *  blocked attempt is still waiting for the optional "was the AI right?" feedback. */
export function ModeratorStatusChip({ status, outcome }: { status: string; outcome?: string | null }) {
  if (status === 'none') {
    return (
      <span className="text-xs text-gray-400">
        {outcome === 'blocked' ? 'No feedback yet' : '—'}
      </span>
    );
  }
  const meta = MOD_STATUS_META[status] ?? { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}
      title={meta.title}
    >
      {meta.label}
    </span>
  );
}

/** Where the content is NOW, which can differ from what happened at check time: a held
 *  post a moderator approved is live, a live post may since have been taken down. */
export const ITEM_STATE_LABEL: Record<string, string> = {
  live: 'Live',
  held: 'Held · only the author sees it',
  rejected: 'Not approved',
  removed: 'Taken down',
  author_deleted: 'Deleted by the author',
  never_posted: 'Never posted',
};

const CONFIDENCE_CLS: Record<string, string> = {
  high: 'text-red-600',
  medium: 'text-amber-600',
  low: 'text-gray-500',
};

/** One line per rule the AI cited. `fix` is what the employee was told; `reason` is the
 *  moderator-facing explanation, so both are shown and labelled — a moderator judging
 *  the verdict needs the reasoning, and one handling an escalation needs the wording the
 *  employee actually saw. Photo numbers are 1-based, matching the author's message. */
export function ViolationList({ violations }: { violations: Violation[] }) {
  if (violations.length === 0) {
    return <div className="text-xs text-gray-400">No rules cited.</div>;
  }
  return (
    <ul className="space-y-2">
      {violations.map((v, i) => (
        <li key={`${v.ruleId}:${i}`} className="rounded-lg border border-gray-100 bg-white p-2.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-medium text-gray-800">{v.ruleName || v.ruleId}</span>
            {/* Same wording as the policy page's rule actions and its test panel. */}
            <span className={`whitespace-nowrap rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
              v.action === 'block' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
            }`}>
              {v.action === 'block' ? 'Block' : 'Hold for review'}
            </span>
            <span className={`text-[11px] font-medium ${CONFIDENCE_CLS[v.confidence] ?? 'text-gray-500'}`}>
              {v.confidence} confidence
            </span>
            {v.mediaIndex != null && (
              <span className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                <ImageIcon className="h-3 w-3" /> Photo {v.mediaIndex + 1}
              </span>
            )}
          </div>
          {v.reason && <p className="mt-1 text-xs text-gray-700">{v.reason}</p>}
          {v.fix && (
            <p className="mt-1 text-xs text-gray-500">
              <span className="font-medium text-gray-600">Told the employee:</span> {v.fix}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

/** What actually happened to the content, in one sentence. The chip says what the AI
 *  thought; this says what the policy MODE did with that thought, which is the part a
 *  moderator new to monitor mode most often gets wrong. */
export function outcomeSentence(r: Pick<AiReviewRow, 'decision' | 'outcome' | 'mode' | 'targetType'>): string {
  const noun = r.targetType === 'comment' ? 'comment' : r.targetType === 'post_edit' ? 'edit' : 'post';
  if (r.outcome === 'blocked') {
    return `Blocked — the ${noun} was never posted. The employee was told which rule it broke and how to fix it.`;
  }
  if (r.outcome === 'held') {
    return `Held — only the author can see this ${noun} until a moderator approves or rejects it.`;
  }
  if (r.outcome === 'published_unchecked') {
    return `The AI couldn't check this ${noun}, so it was published anyway — posting never stops because the AI is unavailable. It needs a human look.`;
  }
  if (r.mode === 'monitor' && (r.decision === 'hold' || r.decision === 'block')) {
    return `Monitor mode — published anyway. In enforce mode this ${noun} would have been ${r.decision === 'block' ? 'blocked' : 'held for review'}.`;
  }
  return `Allowed — the ${noun} was published normally.`;
}

// Through parseServerTimestamp, not `new Date`: these routes hand back raw Postgres
// TIMESTAMPTZ text, which Safari refuses to parse.
const fmtDateTime = (iso: string | null | undefined): string => {
  const d = parseServerTimestamp(iso);
  return !d
    ? '—'
    : d.toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
      });
};

type ActionSet = 'held' | 'live' | 'blocked' | null;

/** Mirrors the decide endpoint's guards (see the header). Deriving it from the same
 *  three fields the server checks is what keeps a button from being offered for a
 *  transition the server will refuse. */
const actionSetFor = (r: Pick<AiReviewRow, 'moderatorStatus' | 'outcome' | 'itemState'>): ActionSet => {
  if (r.moderatorStatus === 'pending' && r.outcome === 'held' && r.itemState === 'held') return 'held';
  if (r.moderatorStatus === 'pending' && r.itemState === 'live') return 'live';
  if (r.moderatorStatus === 'none' && r.outcome === 'blocked') return 'blocked';
  return null;
};

const DONE_MESSAGE: Record<AiReviewAction, string> = {
  approve: 'Approved — it is now published.',
  // A LIVE item's reject is a takedown; decide() words that case itself.
  reject: 'Rejected — the author now sees it as not approved.',
  dismiss: 'Marked as fine.',
  confirm: 'Thanks — recorded that the AI was right.',
  overturn: 'Thanks — recorded that the AI was wrong.',
};

// ═════════════════════════════════════════════════════════════════════════════

interface Props {
  /** The review row's id. BIGSERIAL: a string from the list, a number from getItem's
   *  aiReview — it is only ever sent back to the server, so both are accepted. */
  reviewId: AiReviewRow['id'] | number;
  projectId: number;
  onClose: () => void;
  /** Fired after any decision so the list behind can refetch. */
  onChanged: () => void;
}

export default function AiReviewModal({ reviewId, projectId, onClose, onChanged }: Props) {
  const [detail, setDetail] = useState<AiReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<AiReviewAction | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [confirmTakedown, setConfirmTakedown] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr(null);
    connectPolicyAPI
      .getAiReview(projectId, reviewId)
      .then((d) => setDetail(d))
      .catch((e: unknown) => setLoadErr(connectPolicyErrorMessage(e, 'Failed to load this AI check')))
      .finally(() => setLoading(false));
  }, [reviewId, projectId]);
  useEffect(() => { load(); }, [load]);

  // Close on Escape, unless the takedown confirmation (which owns Escape) is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !confirmTakedown && !lightbox) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, confirmTakedown, lightbox]);

  const actionSet = detail ? actionSetFor(detail) : null;
  const trimmed = note.trim();

  /** Photo indexes the AI pointed at, so their tiles can be ringed. */
  const flagged = useMemo(() => {
    const s = new Set<number>();
    (detail?.violations ?? []).forEach((v) => { if (v.mediaIndex != null) s.add(v.mediaIndex); });
    return s;
  }, [detail]);

  const decide = async (action: AiReviewAction) => {
    if (!detail) return;
    setBusy(action);
    setActionErr(null);
    setDone(null);
    try {
      await connectPolicyAPI.decideAiReview(projectId, detail.id, action, trimmed || undefined);
      setNote('');
      setDone(action === 'reject' && actionSet === 'live'
        ? `Taken down — the ${itemNoun} is no longer on the feed.`
        : action === 'dismiss' && actionSet === null
        ? 'Cleared from the queue.'
        : DONE_MESSAGE[action]);
      onChanged();
      load();
    } catch (e: unknown) {
      const status = connectPolicyErrorStatus(e);
      setActionErr(connectPolicyErrorMessage(e, 'The action could not be completed'));
      // 409 = someone else decided first, or the author edited/deleted it meanwhile.
      // Reload so the modal stops offering buttons for a state that no longer exists.
      if (status === 409) { onChanged(); load(); }
    } finally {
      setBusy(null);
    }
  };

  const who = detail?.author?.name || detail?.author?.empCode || 'the author';
  // Two nouns: what was CHECKED ("edit" — the blocked-feedback copy is about the edit
  // not being saved) and what a takedown REMOVES (the post the edit belongs to).
  const noun = detail ? (AI_TARGET_LABEL[detail.targetType] ?? 'Item').toLowerCase() : 'item';
  const itemNoun = detail?.targetType === 'comment' ? 'comment' : detail?.targetType === 'reshare' ? 'repost' : 'post';

  return (
    // Close only on a DIRECT backdrop click — a click inside the lightbox or the
    // confirmation must not bubble up and close the whole review.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label="AI content check"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-gray-100 p-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900">
              {detail?.author?.name || 'AI check'}
              {detail?.author?.empCode && (
                <span className="font-mono text-xs font-normal text-gray-400">{detail.author.empCode}</span>
              )}
              {detail && (
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${AI_TARGET_CLS[detail.targetType] ?? 'bg-gray-100 text-gray-600'}`}>
                  {AI_TARGET_LABEL[detail.targetType] ?? detail.targetType}
                </span>
              )}
              {detail && <ModeratorStatusChip status={detail.moderatorStatus} outcome={detail.outcome} />}
            </div>
            {detail && (
              <div className="mt-0.5 text-sm text-gray-500">
                {[detail.author?.designation, detail.author?.storeName].filter(Boolean).join(' · ') || 'No store on record'}
                {' · '}checked {fmtDateTime(detail.createdAt)}
                {' · '}{ITEM_STATE_LABEL[detail.itemState] ?? detail.itemState}
              </div>
            )}
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && !detail ? (
          <BlockLoader className="h-48" />
        ) : loadErr && !detail ? (
          <div className="p-6 text-center text-sm text-red-600">{loadErr}</div>
        ) : detail ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <div className="grid gap-5 lg:grid-cols-[3fr,2fr]">
              {/* Left — what was submitted */}
              <div className="space-y-4">
                {detail.parentPostPreview && (
                  // Context the AI was given "for meaning only": the post a comment
                  // replies to, or the one a repost quotes. Shown so the moderator judges
                  // with the same context — and styled apart so nobody judges IT.
                  <section className="rounded-lg border border-dashed border-gray-200 p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {detail.targetType === 'comment' ? 'Comment on this post' : 'Quoting this post'}
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-xs text-gray-500">{detail.parentPostPreview}</p>
                  </section>
                )}

                <section className={`rounded-lg border p-4 ${
                  detail.outcome === 'blocked' ? 'border-red-100 bg-red-50/40' : 'border-gray-100 bg-gray-50/60'
                }`}>
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {detail.outcome === 'blocked' ? `What ${who} tried to post` : `${AI_TARGET_LABEL[detail.targetType] ?? 'Content'} by ${who}`}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">
                    {detail.content || <span className="text-gray-400">No text</span>}
                  </p>

                  {(detail.media?.length ?? 0) > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3">
                      {detail.media.map((m) => (
                        <figure key={m.index} className="w-24">
                          <AuthedImage
                            path={m.url}
                            alt={`Photo ${m.index + 1}`}
                            className={`h-24 w-24 cursor-pointer rounded-md object-cover ${
                              flagged.has(m.index) ? 'ring-2 ring-red-500 ring-offset-1' : ''
                            }`}
                            onClick={() => setLightbox(m.url)}
                          />
                          <figcaption className={`mt-0.5 text-center text-[11px] ${
                            flagged.has(m.index) ? 'font-semibold text-red-600' : 'text-gray-400'
                          }`}>
                            Photo {m.index + 1}{flagged.has(m.index) ? ' · flagged' : ''}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  )}
                </section>

                {/* The decision record, once there is one. */}
                {detail.moderatedAt && (
                  <div className="rounded-lg border border-gray-100 p-3 text-xs text-gray-600">
                    <span className="font-medium text-gray-800">
                      {MOD_STATUS_META[detail.moderatorStatus]?.label ?? detail.moderatorStatus}
                    </span>
                    {detail.moderatorEmpCode ? ` by ${detail.moderatorEmpCode}` : ''} on {fmtDateTime(detail.moderatedAt)}
                    {detail.moderatorNote && <div className="mt-1 text-gray-700">“{detail.moderatorNote}”</div>}
                  </div>
                )}
              </div>

              {/* Right — the verdict */}
              <AiVerdictPanel
                decision={detail.decision}
                mode={detail.mode}
                summary={detail.summary}
                violations={detail.violations ?? []}
                error={detail.error}
                footnote={
                  [
                    detail.policyVersion != null ? (detail.policyVersion === 0 ? 'Built-in default policy' : `Policy v${detail.policyVersion}`) : null,
                    detail.model || null,
                    detail.latencyMs != null ? `${(detail.latencyMs / 1000).toFixed(1)} s` : null,
                  ].filter(Boolean).join(' · ')
                }
                sentence={outcomeSentence(detail)}
              />
            </div>
          </div>
        ) : null}

        {/* Footer — the decision this state allows */}
        {detail && (
          <div className="shrink-0 border-t border-gray-100 p-4">
            {actionErr && (
              <div className="mb-3 flex items-start gap-2 rounded-md bg-red-50 p-2.5 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-none" /> <span>{actionErr}</span>
              </div>
            )}
            {done && !actionErr && (
              <div className="mb-3 flex items-start gap-2 rounded-md bg-green-50 p-2.5 text-sm text-green-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" /> <span>{done}</span>
              </div>
            )}

            {actionSet === 'held' && (
              <>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ai-review-note">
                  Reason (shown to the employee)
                  <span className="ml-1 font-normal text-gray-400">
                    used only if you reject — optional; left blank they see “Not approved by a moderator”.
                  </span>
                </label>
                <textarea
                  id="ai-review-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="e.g. Please remove the customer's phone number and post again."
                  className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
                <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => decide('reject')}
                    disabled={busy != null}
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    {busy === 'reject' ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => decide('approve')}
                    disabled={busy != null}
                    className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {busy === 'approve' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Approve and publish
                  </button>
                </div>
              </>
            )}

            {actionSet === 'live' && (
              <>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ai-review-note">
                  Removal reason <span className="text-red-600">*</span>
                  <span className="ml-1 font-normal text-gray-400">
                    required to take it down — it is the record shown if the employee escalates.
                  </span>
                </label>
                <textarea
                  id="ai-review-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="Why is this being removed? (also kept as the note if you mark it fine)"
                  className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
                <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => decide('dismiss')}
                    disabled={busy != null}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {busy === 'dismiss' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Looks fine
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmTakedown(true)}
                    disabled={busy != null || !trimmed}
                    title={!trimmed ? 'Enter a removal reason first' : `Remove this ${itemNoun} from the feed`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    <ShieldOff className="h-4 w-4" /> {busy === 'reject' ? 'Removing…' : 'Take down'}
                  </button>
                </div>
              </>
            )}

            {actionSet === 'blocked' && (
              <>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ai-review-note">
                  Note <span className="font-normal text-gray-400">optional — helps whoever tunes the policy</span>
                </label>
                <textarea
                  id="ai-review-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="e.g. This was an MRP tag, not a customer's number."
                  className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  {/* Says what the buttons do NOT do, because "AI was wrong" reads like
                      it should publish the post. It can't: nothing was saved to publish. */}
                  <span className="text-xs text-gray-500">
                    Feedback only — the {noun} was never saved, so nothing is published or changed.
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => decide('overturn')}
                      disabled={busy != null}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {busy === 'overturn' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ThumbsDown className="h-4 w-4" />}
                      AI was wrong
                    </button>
                    <button
                      type="button"
                      onClick={() => decide('confirm')}
                      disabled={busy != null}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {busy === 'confirm' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ThumbsUp className="h-4 w-4" />}
                      AI was right
                    </button>
                  </div>
                </div>
              </>
            )}

            {actionSet === null && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-500">
                  {detail.moderatorStatus === 'pending'
                    // Pending, but the content moved on (taken down from Reported posts,
                    // deleted by the author) — there is no longer anything to decide here.
                    ? `This ${noun} is no longer ${detail.outcome === 'held' ? 'held' : 'live'}, so there is nothing left to decide.`
                    : detail.moderatorStatus === 'none'
                    ? 'Nothing to review — the AI allowed this.'
                    : 'Already decided.'}
                </span>
                <div className="flex items-center gap-2">
                  {/* Pending on content that has gone (deleted, taken down, rejected via
                      another check): the server accepts `dismiss` for exactly this, and
                      without it the check would sit in "Needs review" for good. Not for
                      content that is held by a LATER check — that one is decided there. */}
                  {detail.moderatorStatus === 'pending' && detail.itemState !== 'held' && (
                    <button
                      type="button"
                      onClick={() => decide('dismiss')}
                      disabled={busy != null}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {busy === 'dismiss' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Clear from queue
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {lightbox && <ReviewLightbox path={lightbox} onClose={() => setLightbox(null)} />}

      <ConfirmDeleteModal
        isOpen={confirmTakedown}
        onClose={() => setConfirmTakedown(false)}
        onConfirm={() => {
          setConfirmTakedown(false);
          decide('reject');
        }}
        isSubmitting={busy === 'reject'}
        title="Take down content"
        entityLabel={`${itemNoun} by`}
        entityName={who}
        // Same soft-delete takedown as Reported posts: reversible from that queue, so
        // the default "cannot be undone" copy would be wrong here too.
        consequenceNote="It disappears from the feed immediately. You can restore it from Reported posts."
        confirmLabel="Take down"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/** The AI side of a check: verdict chip, what that meant, the summary and each cited
 *  rule. Exported for ReviewModal, which shows the same panel for a feed post that went
 *  through a check (it passes no footnote, since getItem carries no model/latency). */
export function AiVerdictPanel({ decision, mode, summary, violations, error, footnote, sentence, children }: {
  decision: string;
  mode?: string | null;
  summary: string | null | undefined;
  violations: Violation[];
  error?: string | null;
  footnote?: string | null;
  sentence?: string | null;
  children?: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
          <Sparkles className="h-3.5 w-3.5" /> AI check
        </div>
        <AiDecisionChip decision={decision} mode={mode} />
      </div>
      {sentence && <p className="mb-2 text-xs text-gray-600">{sentence}</p>}
      {decision === 'error' && error && (
        <p className="mb-2 rounded-md bg-white px-2 py-1.5 text-xs text-gray-500">
          Why it wasn't checked: {error}
        </p>
      )}
      {summary && <p className="mb-2 text-sm text-gray-800">{summary}</p>}
      {decision !== 'error' && decision !== 'allow' && <ViolationList violations={violations} />}
      {footnote && <div className="mt-2 text-[11px] text-gray-400">{footnote}</div>}
      {children}
    </section>
  );
}

/** Full-size view of a review photo. ImageLightbox takes a plain `src`, and review
 *  media is bearer-gated, so the blob is fetched first (see AuthedImage). */
function ReviewLightbox({ path, onClose }: { path: string; onClose: () => void }) {
  const { url, failed } = useAuthedImageUrl(path);
  if (url) return <ImageLightbox src={url} alt="Photo, full size" isOpen onClose={onClose} />;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80" onClick={onClose}>
      {failed
        ? <span className="text-sm text-white/80">Image could not be loaded.</span>
        : <Loader2 className="h-6 w-6 animate-spin text-white/80" />}
    </div>
  );
}
